import { useCallback } from "react";

/** Put this on the wrapper of any field that is currently rendering a validation error. */
export const ERROR_ANCHOR_CLASS = "error-anchor";

const FOCUSABLE_SELECTOR = "input, select, textarea, [tabindex]:not([tabindex='-1'])";
const STICKY_FOOTER_GAP = 16;
/** Error markup lands a frame or two after the continue click, once the machine transition renders. */
const MAX_LOOKUP_FRAMES = 5;

/** Extra space reserved below the error so it clears the fixed footer button bar. */
function getStickyFooterOffset() {
  const footer = document.querySelector<HTMLElement>("[data-stepper-footer]");
  if (!footer) return 0;
  return footer.getBoundingClientRect().height + STICKY_FOOTER_GAP;
}

/** Topmost error anchor in document order, ignoring ones that are not currently visible. */
function findFirstErrorElement() {
  const candidates = document.querySelectorAll<HTMLElement>(`.${ERROR_ANCHOR_CLASS}`);
  for (const candidate of candidates) {
    if (candidate.getClientRects().length > 0) return candidate;
  }
  return null;
}

interface UseScrollToFirstErrorOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
}

/** Returns a callback for the continue button: scrolls to the first field marked as invalid. */
export function useScrollToFirstError({
  behavior = "smooth",
  block = "center",
}: UseScrollToFirstErrorOptions = {}) {
  return useCallback(() => {
    const attempt = (remainingFrames: number) => {
      const errorEl = findFirstErrorElement();

      if (!errorEl) {
        if (remainingFrames > 0) {
          requestAnimationFrame(() => attempt(remainingFrames - 1));
        }
        return;
      }

      const focusTarget = errorEl.matches(FOCUSABLE_SELECTOR)
        ? errorEl
        : errorEl.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      focusTarget?.focus({ preventScroll: true });

      const footerOffset = getStickyFooterOffset();
      if (footerOffset > 0) {
        errorEl.style.scrollMarginBottom = `${footerOffset}px`;
      }
      errorEl.scrollIntoView({ behavior, block });
    };

    attempt(MAX_LOOKUP_FRAMES);
  }, [behavior, block]);
}
